import SystemLogo from '@shared/assets/svg/system-logo-light.svg?react';
import SystemLogoMark from '@shared/assets/svg/system-logo-mark.svg?react';
import { ClockIcon } from '@shared/icons/Clock';
import { DashboardIcon } from '@shared/icons/Dashboard';
import { EditIcon } from '@shared/icons/Edit';
import { IconDoubleAltArrowLeft } from '@shared/icons/IconDoubleAltArrowLeft';
import { IconDoubleAltArrowRight } from '@shared/icons/IconDoubleAltArrowRight';
import { IconQuestionCircle } from '@shared/icons/IconQuestionCircle';
import { MoonIcon } from '@shared/icons/Moon';
import { NotificationIcon } from '@shared/icons/Notification';
import { SunIcon } from '@shared/icons/Sun';
import { Link, useLocation } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { IconDetailEstimation } from '@/assets/Icons/IconDetailEstimation.tsx';
import { IconWizardMode } from '@/assets/Icons/IconWizardMode';
import Dropdown from '@/components/atoms/Dropdown';
import { LanguageSelector } from '@/components/molecules/LanguageSelector';
import { BuildInformation } from '@/components/organism/BuildInformation';
import { useNotifications } from '@/components/organism/Header/NotificationsDropdown/useNotifications';
import TipsNewsDropdown from '@/components/organism/Header/TipsNewsDropdown';
import AdministrationSidebar from '@/components/organism/Sidebar/administration';
import AiAdvisorySidebar from '@/components/organism/Sidebar/aiAdvisory';
import { TextDirection } from '@/constants';
import { useAiSettings } from '@/context/aiSettings';
import { useAuth } from '@/context/auth';
import { useEstimation } from '@/context/estimationContext';
import { useLocalization } from '@/context/localization';
import { Theme, useTheme } from '@/context/theme';
import { useLanguageDirection } from '@/hooks/languages/useLanguages.ts';
import { useMessages } from '@/hooks/messages';
import { useHasGlobalPermission } from '@/hooks/useHasGlobalPermission';
import { useLocationMatch } from '@/hooks/useLocationMatch';
import { useTenantAssetRefresh } from '@/hooks/useTenantAssetRefresh';
import { isLargeScreen, useWindowDimensions } from '@/hooks/useWindowDimensions';
import { NavigationMessages } from '@/pages/estimation/components/estimationNavigation/messages';
import { getWizardSteps } from '@/pages/estimation/components/estimationNavigation/model';
import { SetupNavigation } from '@/pages/estimation/components/estimationNavigation/SetupNavigation';
import { WizardModeNavigation } from '@/pages/estimation/components/estimationNavigation/WizardModeNavigation';
import type { AvailableRoutes } from '@/types';

import HelpModal from '../HelpModal';
import LogoutButton from '../LogoutButton';
import { messages } from './messages';

export type LinkProps = {
  className: string;
  activeProps: {
    className: string;
  };
  activeOptions: {
    exact: boolean;
  };
};

interface SidebarProps {
  isOpen?: boolean;
  onToggle?: (isOpen: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onToggle }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);

  const { tenant, user } = useAuth();
  // EWM-548: re-fetch a fresh SAS logo URL if the stored one has expired (403 -> img error).
  const { onAssetError: onLogoError, reset: resetLogoRefresh } = useTenantAssetRefresh();
  const { isAiEnabled } = useAiSettings();
  const message = useMessages(messages);
  const { theme, setTheme } = useTheme();
  const { pathname } = useLocation();
  const tenantLogoUrl = (theme === Theme.light ? tenant?.logoUrl : tenant?.darkModeLogoUrl) || null;
  const { hasPartialMatch } = useLocationMatch();
  const { rtlClass, setLangDirection } = useLanguageDirection();
  const {
    state: { languageList },
  } = useLocalization();

  const {
    state: { detailedEstimationId, savedEstimation },
    actions: { resetEstimationState },
  } = useEstimation();

  const navigationMessages = useMessages(NavigationMessages);
  const wizardSteps = useMemo(() => getWizardSteps(navigationMessages), [navigationMessages]);
  const showWizardModeSection =
    !!savedEstimation?.estimation.id && wizardSteps.some((s) => s.link && hasPartialMatch(s.link));

  const { getNotifications } = useNotifications();
  const { data: notifications } = getNotifications();
  const unreadCount = notifications?.filter((notification) => !notification.isRead).length || 0;

  const { width } = useWindowDimensions();
  const isLargerScreen = isLargeScreen(width);

  // Use external state if provided, otherwise use internal state
  const sidebarOpen = isOpen !== undefined ? isOpen : isSidebarOpen;
  const setSidebarOpen = (open: boolean) => {
    if (isOpen !== undefined) {
      onToggle?.(open);
    } else {
      setIsSidebarOpen(open);
    }
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleMenuItemClick = () => {
    // Auto-collapse sidebar only on small screens (mobile devices)
    if (!isLargerScreen) {
      setSidebarOpen(false);
    }
  };

  const hasAdminPermissions = [
    useHasGlobalPermission('CAN_MANAGE_ESTIMATION_METHODOLOGIES'),
    useHasGlobalPermission('CAN_MANAGE_ESTIMATION_MODELS'),
    useHasGlobalPermission('CAN_MANAGE_INDIVIDUALISATION'),
    useHasGlobalPermission('CAN_MANAGE_PROJECT_TYPES'),
    useHasGlobalPermission('CAN_MANAGE_TENANT_SETTINGS'),
    useHasGlobalPermission('CAN_MANAGE_USERS'),
    useHasGlobalPermission('CAN_MANAGE_WORKFLOW'),
  ].some((x) => x);

  const getLinkProps = (display: 'block' | 'inline-flex' = 'inline-flex'): LinkProps => ({
    activeOptions: {
      exact: true,
    },
    activeProps: {
      className: `!text-(--primary) ${theme === Theme.dark ? '!bg-(--color-dark-grey)' : '!bg-(--color-light-grey)'}`,
    },
    className: `${display} w-full items-center gap-2 rounded-2xl p-4 mb-2 cursor-pointer ${theme === Theme.dark ? 'text-(--color-white) hover:bg-(--color-dark-grey)' : 'text-(--color-black) hover:bg-(--color-light-grey)'}`,
  });

  const linkProps = getLinkProps();
  const blockLinkProps = getLinkProps('block');
  return (
    <>
      {/* Overlay for small screens when sidebar is open */}
      {!isLargerScreen && sidebarOpen && (
        <button
          type='button'
          aria-label='Close sidebar'
          className='fixed inset-0 z-[150] cursor-default bg-black/50 backdrop-blur-xs'
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <section
        className={`sidebar-container dark:bg-c16 fixed left-1 z-[200] mr-2 flex overflow-x-hidden rounded-2xl ${
          sidebarOpen ? 'w-[350px]' : 'w-[72px]'
        } shadow-steps dark:shadow-steps-dark relative flex-col bg-(--color-white) p-2 pt-8 transition-all duration-300 ease-in-out lg:relative lg:z-auto ${!isLargerScreen && !sidebarOpen ? '-translate-x-full' : 'translate-x-0'} } top-1`}
      >
        <div
          className={`w-full px-0 py-4 ${!isLargerScreen ? 'hidden' : 'flex'} ${
            sidebarOpen ? 'items-center justify-between' : 'flex-col items-center gap-3'
          }`}
        >
          <Link to='/' className='flex min-w-0 items-center justify-center'>
            {/*
              EWM-xxx: the collapsed rail is 72px wide, which is not enough for any wordmark - the
              tenant logo used to be sized `w-auto max-w-full` here and computed to 0x0, because
              nothing in this flex chain has a definite width for `max-w-full` to resolve against.
              A tenant that has uploaded no logo now gets the EW symbol rather than a broken image.
              A tenant that HAS one still gets its full logo scaled down, because the API carries
              no short-form tenant logo (ApiLogoType is NORMAL | DARK_MODE only).
            */}
            {tenantLogoUrl ? (
              <img
                src={tenantLogoUrl}
                className={`object-contain opacity-100 transition-all duration-300 ${
                  sidebarOpen ? 'h-10 w-auto max-w-[280px] pl-1' : 'h-auto w-12'
                }`}
                alt='Logo'
                onError={onLogoError}
                onLoad={resetLogoRefresh}
              />
            ) : sidebarOpen ? (
              <SystemLogo className='h-10 w-auto max-w-[280px] pl-1' />
            ) : (
              <SystemLogoMark className='h-auto w-9' />
            )}
          </Link>
          <button
            onClick={toggleSidebar}
            className='flex h-8 w-8 shrink-0 items-center justify-center'
            data-testid='sidebar-toggle'
          >
            {sidebarOpen ? <IconDoubleAltArrowLeft /> : <IconDoubleAltArrowRight />}
          </button>
        </div>

        <div
          className={`flex-1 items-center gap-2 rounded-tr-lg ${!isLargerScreen && !sidebarOpen ? 'hidden' : 'block'}`}
        >
          <Link to='/' {...linkProps} onClick={handleMenuItemClick}>
            <div className='h-6 w-6'>
              <DashboardIcon className='text-c1' />
            </div>
            <span
              className={`font-semibold transition-opacity duration-200 ${sidebarOpen ? 'opacity-100' : 'opacity-0'} whitespace-nowrap`}
            >
              {message.dashboard()}
            </span>
          </Link>
          <Link to='/estimations' {...linkProps} onClick={handleMenuItemClick}>
            <div className='h-6 w-6'>
              <ClockIcon className='text-c1' />
            </div>
            <span
              className={`font-semibold transition-opacity duration-200 ${sidebarOpen ? 'opacity-100' : 'opacity-0'} whitespace-nowrap`}
            >
              {message.estimation()}
            </span>
          </Link>
          {/* AI Advisory - conditionally shown based on AI settings */}
          {isAiEnabled && (
            <div className={blockLinkProps.className}>
              <AiAdvisorySidebar
                linkProps={linkProps}
                isSidebarOpen={sidebarOpen}
                onItemClick={handleMenuItemClick}
              />
            </div>
          )}
          {/* Create New Estimate - Setup steps (Project, Type, Methodology) */}
          <div className={blockLinkProps.className}>
            <Link
              activeOptions={{ exact: false }}
              to='/estimation/new'
              onClick={resetEstimationState}
            >
              <div className='w-full'>
                <div className='flex gap-2'>
                  <div className='h-6 w-6'>
                    <EditIcon className='text-c1' />
                  </div>
                  <span
                    className={`font-semibold transition-opacity duration-200 ${sidebarOpen ? 'opacity-100' : 'opacity-0'} whitespace-nowrap`}
                  >
                    {message.newEstimation()}
                  </span>
                </div>
              </div>
            </Link>
            <SetupNavigation isSidebarOpen={sidebarOpen} onItemClick={handleMenuItemClick} />
          </div>
          {/* Wizard Mode - only visible after user chooses wizard mode */}
          {showWizardModeSection && (
            <div className={blockLinkProps.className}>
              <div className='w-full'>
                <div className='flex gap-2'>
                  <div className='h-6 w-6'>
                    <IconWizardMode className='text-c1' />
                  </div>
                  <span
                    className={`font-semibold transition-opacity duration-200 ${sidebarOpen ? 'opacity-100' : 'opacity-0'} whitespace-nowrap`}
                  >
                    {message.wizardMode()}
                  </span>
                </div>
              </div>
              <WizardModeNavigation isSidebarOpen={sidebarOpen} onItemClick={handleMenuItemClick} />
            </div>
          )}
          <Link
            {...linkProps}
            activeOptions={{ exact: false }}
            to={`/estimation/detail/${detailedEstimationId}` as AvailableRoutes}
            disabled={!detailedEstimationId}
            onClick={handleMenuItemClick}
            data-testid='nav-detailed-estimation'
            className={
              pathname.includes('/estimation/detail/')
                ? linkProps.className
                : `${linkProps.className} ${!detailedEstimationId ? 'text-secondary' : ''}`
            }
          >
            <div className='h-6 w-6'>
              <IconDetailEstimation className='text-c1' />
            </div>
            <span
              className={`font-semibold transition-opacity duration-200 ${sidebarOpen ? 'opacity-100' : 'opacity-0'} whitespace-nowrap`}
            >
              {message.detailedEstimation()}
            </span>
          </Link>
          <Link
            to='/notifications'
            {...linkProps}
            onClick={handleMenuItemClick}
            data-testid='notifications-link'
          >
            <div className='relative h-6 w-6'>
              <NotificationIcon className='text-c1' />
              {unreadCount > 0 && (
                <span className='absolute -top-1 -right-1 flex h-5 w-5 animate-pulse items-center justify-center rounded-full bg-red-500 text-xs text-white'>
                  {unreadCount}
                </span>
              )}
            </div>
            <span
              className={`font-semibold transition-opacity duration-200 ${sidebarOpen ? 'opacity-100' : 'opacity-0'} whitespace-nowrap`}
            >
              {message.notification()}
            </span>
          </Link>
          {hasAdminPermissions && (
            <AdministrationSidebar
              linkProps={blockLinkProps}
              isSidebarOpen={sidebarOpen}
              onItemClick={handleMenuItemClick}
            />
          )}
        </div>

        <div className='mt-auto flex flex-col gap-4 p-2'>
          <button
            type='button'
            aria-label='Open help'
            className='dark:bg-dark/40 dark:hover:bg-dark/60 flex h-10 w-10 cursor-pointer items-center rounded-full bg-gray-100 p-2 hover:bg-gray-200'
            onClick={() => setIsHelpModalOpen(true)}
          >
            <IconQuestionCircle />
          </button>
          <button
            onClick={() => setTheme()}
            className='dark:bg-dark/40 dark:hover:bg-dark/60 flex h-10 w-10 items-center rounded-full bg-gray-100 p-2 hover:bg-gray-200'
            data-testid='theme-toggle-button'
          >
            {theme === Theme.dark ? <SunIcon /> : <MoonIcon />}
          </button>

          <LanguageSelector onChange={setLangDirection} availableLocales={languageList} />

          <TipsNewsDropdown />

          <div className='dropdown flex shrink-0 gap-5'>
            <Dropdown
              offset={[0, 8]}
              poperClass='z-[10000]'
              btnClassName='relative group block'
              placement={rtlClass === TextDirection.RTL ? 'bottom-start' : 'bottom-end'}
              button={
                <span
                  className='dark:bg-dark/40 dark:hover:bg-dark/60 flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 object-cover hover:bg-gray-200'
                  title='userProfile'
                  data-testid='user-profile-button'
                >
                  {user?.forename.charAt(0)}
                  {user?.surname.charAt(0)}
                </span>
              }
            >
              <ul
                className='w-[300px] !py-0 font-semibold dark:text-white'
                data-testid='user-profile-menu'
              >
                <li>
                  <div className='dark:bg-c4 flex items-center px-4 py-4'>
                    <span
                      className='dark:bg-c16 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-100 object-cover'
                      title='userProfile'
                    >
                      {user?.forename.charAt(0)}
                      {user?.surname.charAt(0)}
                    </span>

                    <div className='truncate pl-4'>
                      <h4 className='text-base'>
                        {user?.forename} {user?.surname}
                      </h4>
                      <span className='dark:text-dark-light/60 text-black/60 hover:text-black dark:hover:text-white'>
                        {user?.email}
                      </span>
                    </div>
                  </div>
                </li>
                <li className='border-white-light dark:border-white-light/10 dark:bg-c4 border-t'>
                  <LogoutButton />
                </li>
              </ul>
            </Dropdown>
            <span className={`pt-2 ${!sidebarOpen ? 'hidden' : 'block'}`}>
              {user?.forename} {user?.surname}
            </span>
          </div>
        </div>
        <BuildInformation />

        <HelpModal isOpen={isHelpModalOpen} onClose={() => setIsHelpModalOpen(false)} />
      </section>
    </>
  );
};

export default Sidebar;
